import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	TextField,
	Box,
	InputAdornment,
	Button,
	Tooltip,
	Stack,
	FormControlLabel,
	Checkbox,
	Divider,
} from '@mui/material';
import { DEFAULT_JOB_FORM_DATA } from '../../Shared/jobs';
import { Add as AddIcon } from '@mui/icons-material';
import { makeApiRequest } from '../../Shared/Api';
import { refreshOnboardingChecklist } from '../../Shared/onboarding';
import CustomSelect from '../Inputs/CustomSelect';
import EditCustomer from './EditCustomer';
import Modal from './Modal';
import StripeFeeQuoteHelper from '../StripeFeeQuoteHelper';
import LineItemsEditor from '../LineItems/LineItemsEditor';
import BidFormatToggle from '../LineItems/BidFormatToggle';
import { useSnackbar } from 'notistack';
import { useStripePaymentsEnabled } from '../../Context/AppContext';
import { useDefaultItemizedBids } from '../../Hooks/useFeature';
import {
	completeLineItems,
	lineItemsGrandTotal,
	serializeLineItemsForApi,
} from '../../Shared/lineItems';

const CreateJob = ({ open, onClose, onJobCreated }) => {
	const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState(DEFAULT_JOB_FORM_DATA);
	const [customers, setCustomers] = useState([]);
	const [lineItemsDraft, setLineItemsDraft] = useState([]);
	const [useItemizedBid, setUseItemizedBid] = useState(false);
	const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const stripePaymentsEnabled = useStripePaymentsEnabled();
	const defaultItemizedBids = useDefaultItemizedBids();

	const fetchCustomers = useCallback(async () => {
		try {
			const response = await makeApiRequest('GET', '/customers/');
			setCustomers(response || []);
		} catch (error) {
			enqueueSnackbar(
				error.message || 'Failed to fetch customers. Please try again.',
				{ variant: 'error' },
			);
		}
	}, [enqueueSnackbar]);

	useEffect(() => {
		if (open) {
			fetchCustomers();
			setUseItemizedBid(defaultItemizedBids);
			setLineItemsDraft([]);
		}
	}, [open, fetchCustomers, defaultItemizedBids]);

	const hasBidAmount = Number(formData.bid_amount) > 0;
	const lineItemsTotal = useMemo(
		() => lineItemsGrandTotal(lineItemsDraft),
		[lineItemsDraft],
	);
	const hasLineItemsTotal = lineItemsTotal > 0;
	const showingItemized = useItemizedBid;
	const canSendBid = showingItemized ? hasLineItemsTotal : hasBidAmount;

	const handleBidFormatChange = (itemized) => {
		setUseItemizedBid(itemized);
		setFormData((prev) => ({
			...prev,
			send_bid: itemized ? hasLineItemsTotal : hasBidAmount,
		}));
	};

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;

		if (name === 'customer') {
			const selectedCustomer = customers.find((c) => c.id === value);
			setFormData((prev) => ({
				...prev,
				customer: value,
				customer_object: selectedCustomer,
			}));
			return;
		}

		if (name === 'bid_amount') {
			const amount = value === '' ? 0 : parseFloat(value);
			setFormData((prev) => ({
				...prev,
				bid_amount: value,
				send_bid: amount > 0,
			}));
			return;
		}

		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	const handleCustomerAdded = (newCustomer) => {
		setCustomers((prev) => [...prev, newCustomer]);
		setFormData((prev) => ({
			...prev,
			customer: newCustomer.id,
			customer_object: newCustomer,
		}));
	};

	const handleOnClose = () => {
		setFormData(DEFAULT_JOB_FORM_DATA);
		setLineItemsDraft([]);
		setUseItemizedBid(defaultItemizedBids);
		onClose();
		setIsCustomerModalOpen(false);
	};

	const handleSubmit = async (e) => {
		if (e) e.preventDefault();

		if (formData.send_bid) {
			if (showingItemized) {
				const completeItems = completeLineItems(lineItemsDraft);
				if (!completeItems.length || lineItemsTotal <= 0) {
					enqueueSnackbar(
						'Add at least one line item with a price before sending a bid.',
						{ variant: 'error' },
					);
					return;
				}
			} else if (!hasBidAmount) {
				enqueueSnackbar('Enter a valid bid amount before sending a bid.', {
					variant: 'error',
				});
				return;
			}
		}

		setLoading(true);

		try {
			const { customer_object, bid_expires_at, ...dataToSend } = formData;
			const response = await makeApiRequest('POST', '/jobs/', {
				...dataToSend,
				...(stripePaymentsEnabled ? {} : { quote_includes_stripe_fee: false }),
				...(bid_expires_at
					? { expires_at: new Date(bid_expires_at).toISOString() }
					: {}),
				itemized_pricing: showingItemized,
				...(showingItemized
					? { line_items: serializeLineItemsForApi(lineItemsDraft) }
					: {}),
				...(formData.deposit_percent
					? { deposit_percent: Number(formData.deposit_percent) }
					: {}),
			});
			if (onJobCreated) onJobCreated(response);
			if (formData.send_bid) {
				refreshOnboardingChecklist();
			}
			handleOnClose();
		} catch (err) {
			enqueueSnackbar(
				err.message || 'Failed to create job. Please try again.',
				{ variant: 'error' },
			);
		} finally {
			setLoading(false);
		}
	};

	const ActionButtons = () => (
		<Stack
			direction="row"
			display="flex"
			justifyContent="space-between"
			alignItems="center"
			sx={{ width: '100%' }}
		>
			<FormControlLabel
				control={
					<Checkbox
						name="send_bid"
						onChange={handleChange}
						checked={formData.send_bid}
						disabled={!canSendBid}
					/>
				}
				label="Send Bid"
			/>
			<Stack direction="row" spacing={1}>
				<Button variant="outlined" onClick={handleOnClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSubmit} disabled={loading}>
					Create Job
				</Button>
			</Stack>
		</Stack>
	);

	const modalMaxWidth = useItemizedBid ? 'md' : 'sm';

	return (
		<>
			<Modal
				open={open}
				onClose={handleOnClose}
				onSubmit={handleSubmit}
				modalTitle="Add New Job"
				submitText={loading ? 'Creating...' : 'Create Job'}
				maxWidth={modalMaxWidth}
				alwaysMaxWidth
				useActionButtons
				actionButtons={<ActionButtons />}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
					<TextField
						fullWidth
						required
						label="Job Name"
						name="name"
						value={formData.name}
						onChange={handleChange}
						disabled={loading}
						autoFocus
					/>

					<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
						<CustomSelect
							name="customer"
							label="Customer"
							value={formData.customer}
							onChange={handleChange}
							items={customers}
							required
							disabled={loading}
							valueField="id"
							labelField="name"
							sx={{ flex: 1 }}
						/>
						<Tooltip title="Create New Customer">
							<Button
								variant="contained"
								sx={{
									height: 56,
									minWidth: { xs: 56, sm: 64 },
									borderRadius: 2,
									flexShrink: 0,
								}}
								onClick={() => setIsCustomerModalOpen(true)}
								disabled={loading}
								aria-label="Add Customer"
							>
								<AddIcon />
							</Button>
						</Tooltip>
					</Box>

					<TextField
						fullWidth
						required
						multiline
						rows={3}
						label="Description"
						name="description"
						value={formData.description}
						onChange={handleChange}
						disabled={loading}
					/>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<BidFormatToggle
							value={useItemizedBid}
							onChange={handleBidFormatChange}
							disabled={loading}
						/>
						{showingItemized ? (
							<>
								<LineItemsEditor
									onItemsChange={setLineItemsDraft}
									compact
									showHint={false}
									quoteIncludesStripeFee={
										stripePaymentsEnabled && formData.quote_includes_stripe_fee
									}
								/>
								{stripePaymentsEnabled ? (
									<StripeFeeQuoteHelper
										lineItems={lineItemsDraft}
										includesFee={formData.quote_includes_stripe_fee}
										disabled={loading}
										onApplyFee={() => {}}
										onIncludesFeeChange={(checked) => {
											setFormData((prev) => ({
												...prev,
												quote_includes_stripe_fee: checked,
											}));
										}}
									/>
								) : null}
							</>
						) : (
							<>
								<TextField
									fullWidth
									label="Bid Amount"
									name="bid_amount"
									type="text"
									inputMode="decimal"
									value={formData.bid_amount}
									onChange={handleChange}
									onBlur={(e) => {
										if (e.target.value === '') {
											setFormData((prev) => ({
												...prev,
												bid_amount: 0,
												send_bid: false,
												quote_includes_stripe_fee: false,
											}));
											return;
										}
										const parsed = parseFloat(e.target.value);
										if (!Number.isNaN(parsed)) {
											setFormData((prev) => ({
												...prev,
												bid_amount: parsed.toFixed(2),
											}));
										}
									}}
									disabled={loading}
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">$</InputAdornment>
											),
										},
									}}
									helperText="One project total on the customer proposal PDF."
								/>

								{stripePaymentsEnabled ? (
									<StripeFeeQuoteHelper
										amount={formData.bid_amount}
										includesFee={formData.quote_includes_stripe_fee}
										disabled={loading}
										onApplyFee={(grossAmount) => {
											const formatted = Number(grossAmount).toFixed(2);
											setFormData((prev) => ({
												...prev,
												bid_amount: formatted,
												send_bid: Number(formatted) > 0,
												quote_includes_stripe_fee: true,
											}));
										}}
										onIncludesFeeChange={(checked) => {
											setFormData((prev) => ({
												...prev,
												quote_includes_stripe_fee: checked,
											}));
										}}
									/>
								) : null}
							</>
						)}
					</Box>

					<Divider />

					<TextField
						label="Deposit required on acceptance"
						name="deposit_percent"
						type="number"
						value={formData.deposit_percent}
						onChange={handleChange}
						disabled={loading || !canSendBid}
						slotProps={{
							input: {
								endAdornment: <InputAdornment position="end">%</InputAdornment>,
								inputProps: { min: 1, max: 100, step: 1 },
							},
						}}
						helperText="Optional. Customer pays this share when they accept; a payment link is emailed automatically."
						fullWidth
					/>

					<TextField
						label="Bid Expiration"
						name="bid_expires_at"
						type="datetime-local"
						value={formData.bid_expires_at}
						onChange={handleChange}
						disabled={loading || !canSendBid}
						slotProps={{ inputLabel: { shrink: true } }}
						helperText={
							canSendBid
								? 'Proposal valid until this date when Send Bid is checked.'
								: showingItemized
									? 'Add line items to set a bid expiration.'
									: 'Enter a bid amount to set an expiration.'
						}
						fullWidth
					/>
				</Box>
			</Modal>

			<EditCustomer
				open={isCustomerModalOpen}
				onClose={() => setIsCustomerModalOpen(false)}
				onSubmit={handleCustomerAdded}
			/>
		</>
	);
};

export default CreateJob;
